import { useEffect } from 'react';
import { hasNotificationsConsent } from '../utils/cookieUtils';
import { loadCannyScript } from '../utils/scriptLoadingUtils';

// Temporary kill switch: set to true to restore the Canny changelog widget and SDK.
export const CANNY_ENABLED = false;

/**
 * Initialize Canny changelog widget
 * Loads script dynamically and initializes only with user consent
 * @param {string} appId - Canny app ID (prod or dev)
 * @returns {Promise<boolean>} True if Canny was initialized
 */
export const initCanny = async (appId) => {
  // Do not load or talk to Canny while the widget is temporarily disabled.
  if (!CANNY_ENABLED) {
    return false;
  }

  const hasConsent = hasNotificationsConsent();

  if (!hasConsent) {
    return false;
  }

  if (typeof window.Canny !== 'function') {
    try {
      await loadCannyScript();

      await new Promise((resolve) => {
        const checkCanny = setInterval(() => {
          if (typeof window.Canny === 'function') {
            clearInterval(checkCanny);
            resolve();
          }
        }, 100);

        setTimeout(() => {
          clearInterval(checkCanny);
          resolve();
        }, 5000);
      });
    } catch (error) {
      return false;
    }
  }

  try {
    window.Canny('initChangelog', {
      appID: appId,
      position: 'top',
      align: 'right',
      theme: 'dark',
    });
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Hook to initialize Canny based on user consent
 */
export const useCanny = () => {
  useEffect(() => {
    if (!CANNY_ENABLED) {
      return;
    }

    const appId = process.env.REACT_APP_ENV_NAME === 'prod'
      ? '688780adbe67c13d05c1470e'
      : '6863bbdcd31852b4e2d4d488';

    initCanny(appId);
  }, []);
};

/**
 * Disable Canny: clear all storage and remove the global so it stops running.
 */
export const disableCanny = () => {
  try {
    clearCannyStorage();
  } catch {
    // Silent fail
  }

  if (window.Canny) {
    delete window.Canny;
  }
};

/**
 * Get all Canny storage keys for cleanup
 * @returns {string[]} Array of localStorage keys used by Canny
 */
export const getCannyStorageKeys = () => {
  const keys = [];
  const cannyPrefixes = ['canny_', 'Canny'];

  Object.keys(localStorage).forEach(key => {
    if (cannyPrefixes.some(prefix => key.startsWith(prefix))) {
      keys.push(key);
    }
  });

  return keys;
};

/**
 * Delete a single cookie across all relevant path/domain combinations.
 */
const nukeCookie = (cookieName) => {
  const paths = ['/', '', '/app', '/login'];
  const hostname = window.location.hostname;
  const domains = ['', hostname, `.${hostname}`];

  const parts = hostname.split('.');
  if (parts.length > 1) {
    const rootDomain = parts.slice(-2).join('.');
    domains.push(rootDomain, `.${rootDomain}`);
  }
  if (parts.length > 2) {
    domains.push(parts.slice(-3).join('.'), `.${parts.slice(-3).join('.')}`);
  }

  paths.forEach(path => {
    domains.forEach(domain => {
      const pathPart = path ? `; path=${path}` : '';
      const domainPart = domain ? `; domain=${domain}` : '';

      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT${pathPart}${domainPart}`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC${pathPart}${domainPart}`;
      document.cookie = `${cookieName}=; max-age=0${pathPart}${domainPart}`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT${pathPart}${domainPart}; SameSite=Lax`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT${pathPart}${domainPart}; SameSite=None; Secure`;
    });
  });
};

/**
 * Delete all Canny-related cookies.
 */
const deleteCannyCookies = () => {
  document.cookie.split(';').forEach(c => {
    const name = c.trim().split('=')[0];
    if (name.toLowerCase().includes('canny')) {
      nukeCookie(name);
    }
  });
};

/**
 * Clear all Canny-related storage and cookies
 */
export const clearCannyStorage = () => {
  const keys = getCannyStorageKeys();
  keys.forEach(key => {
    localStorage.removeItem(key);
  });

  const cannyPrefixes = ['canny_', 'Canny'];
  Object.keys(sessionStorage).forEach(key => {
    if (cannyPrefixes.some(prefix => key.startsWith(prefix))) {
      sessionStorage.removeItem(key);
    }
  });

  deleteCannyCookies();
};

/**
 * Check if there are unseen Canny entries
 * @returns {boolean} True if there are unseen entries
 */
export const hasUnseenCannyEntries = () => {
  // Avoid calling into window.Canny (and any network that implies) while disabled.
  if (!CANNY_ENABLED) {
    return false;
  }

  const hasConsent = hasNotificationsConsent();

  if (!hasConsent || typeof window === 'undefined' || typeof window.Canny !== 'function') {
    return false;
  }

  try {
    return window.Canny('hasUnseenEntries');
  } catch (error) {
    return false;
  }
};